<template>
    <b-container fluid>
        <validation-observer v-slot="{ meta }" :initial-values="workOrderToSave">
            {{ setInvalid(!meta.valid) }}
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-job-type">Job Type:</label>
                </b-col>
                <b-col>
                    <validation-provider name="jobType" v-slot="{ errors }">
                        <form-select
                            id="work-orders-job-type"
                            class="mb-2"
                            v-model="workOrderToSave.jobType"
                            :disabled="!canEdit"
                            :options="jobTypeOptions"
                            :error="errors[0]"
                        ></form-select>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-priority">Priority:</label>
                </b-col>
                <b-col>
                    <validation-provider name="priority" v-slot="{ errors }">
                        <form-select
                            id="work-orders-priority"
                            class="mb-2"
                            v-model="workOrderToSave.priority"
                            :disabled="!canEdit"
                            :options="priorityOptions"
                            :error="errors[0]"
                        ></form-select>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-date-reported">Date Reported:</label>
                </b-col>
                <b-col>
                    <datepicker
                        id="work-orders-date-reported"
                        v-model="workOrderToSave.dateReported"
                        label-help=""
                        :disabled="!canEdit"
                        :hide-header="true"
                        :display-format="userUnicodeDateFormat"
                        class="mb-2"
                    ></datepicker>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-description">Description:</label>
                </b-col>
                <b-col>
                    <validation-provider name="description" rules="noScript" v-slot="{ errors, handleChange }">
                        <form-textarea
                            trim
                            class="mb-2"
                            id="work-orders-description"
                            rows="4"
                            no-resize
                            v-model="workOrderToSave.description"
                            :disabled="!canEdit"
                            :error="errors[0]"
                            @input="handleChange"
                        ></form-textarea>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-start-date">Start Date:</label>
                </b-col>
                <b-col>
                    <datepicker
                        id="work-orders-start-date"
                        v-model="workOrderToSave.startDate"
                        label-help=""
                        :disabled="!canEdit"
                        :hide-header="true"
                        :reset-button="true"
                        :display-format="userUnicodeDateFormat"
                        class="mb-2"
                    >
                    </datepicker>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-completed-date">Completed Date:</label>
                </b-col>
                <b-col>
                    <datepicker
                        id="work-orders-completed-date"
                        v-model="workOrderToSave.completedDate"
                        label-help=""
                        :disabled="!canEdit"
                        :hide-header="true"
                        :display-format="userUnicodeDateFormat"
                        :reset-button="true"
                        class="mb-2"
                    >
                    </datepicker>
                    <b-form-invalid-feedback
                        :style="{
                            display: invalidDateRange ? 'block' : 'none',
                        }"
                    >
                        {{ invalidDateRange ? 'Start Date must be less than Completed Date.' : '' }}
                    </b-form-invalid-feedback>
                </b-col>
            </b-row>
            <div v-if="workOrderToSave.completedDate">
                <b-row class="my-2">
                    <b-col md="2">
                        <label for="work-orders-amount">Amount:</label>
                    </b-col>
                    <b-col>
                        <validation-provider
                            name="amount"
                            v-slot="{ errors, handleChange }"
                            rules="validFloat|required"
                        >
                            <form-input
                                id="work-orders-amount"
                                v-model="workOrderToSave.amount"
                                :disabled="!canEdit"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="my-2">
                    <b-col md="2">
                        <label for="work-orders-invoice-number">Vendor Invoice #:</label>
                    </b-col>
                    <b-col>
                        <b-button-group class="work-orders__group">
                            <b-col md="4" class="work-orders__select">
                                <validation-provider
                                    name="invoiceNumber"
                                    v-slot="{ errors, handleChange }"
                                    rules="validInteger"
                                >
                                    <form-input
                                        class="md-2"
                                        id="work-orders-invoice-number"
                                        v-model="workOrderToSave.invoiceNumber"
                                        :disabled="!canEdit"
                                        :error="errors[0]"
                                        @input="handleChange"
                                    ></form-input>
                                </validation-provider>
                            </b-col>
                            <b-col class="work-orders__option">
                                <validation-provider name="invoiceVendor" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-invoice-vendor"
                                        class="md-2"
                                        v-model="workOrderToSave.invoiceVendor"
                                        :disabled="!canEdit"
                                        :options="invoiceVendorOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                        </b-button-group>
                    </b-col>
                </b-row>
                <b-row class="my-2">
                    <b-col md="2">
                        <label for="work-orders-receipt-number">Store Receipt #:</label>
                    </b-col>
                    <b-col>
                        <b-button-group class="work-orders__group">
                            <b-col md="4" class="work-orders__select">
                                <validation-provider
                                    name="receiptNumber"
                                    v-slot="{ errors, handleChange }"
                                    rules="validInteger"
                                >
                                    <form-input
                                        class="md-2"
                                        id="work-orders-receipt-number"
                                        v-model="workOrderToSave.receiptNumber"
                                        :disabled="!canEdit"
                                        :error="errors[0]"
                                        @input="handleChange"
                                    ></form-input>
                                </validation-provider>
                            </b-col>
                            <b-col class="work-orders__option">
                                <validation-provider name="receiptStore" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-receipt-store"
                                        class="md-2"
                                        v-model="workOrderToSave.receiptStore"
                                        :disabled="!canEdit"
                                        :options="receiptStoreOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                        </b-button-group>
                    </b-col>
                </b-row>
            </div>
            <div>
                <b-row class="my-2">
                    <b-col md="2">
                        <label for="work-orders-assignee-type">Assign To:</label>
                    </b-col>
                    <b-col>
                        <b-button-group class="work-orders__group">
                            <b-col md="4" class="work-orders__select">
                                <validation-provider name="assignee.type" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-assignee-type"
                                        class="md-2"
                                        v-model="workOrderToSave.assignee.type"
                                        :disabled="!canEdit"
                                        :options="filteredAssigneeNotifierTypeOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                            <b-col class="work-orders__option" v-if="workOrderToSave.assignee.type !== ''">
                                <validation-provider name="assignee.value" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-assignee-value"
                                        class="md-2"
                                        v-model="workOrderToSave.assignee.value"
                                        :description="
                                            workOrderToSave.assignee.type === ActorType.Vendor
                                                ? vendorOptionsDescription
                                                : ''
                                        "
                                        :disabled="!canEdit"
                                        :options="assigneeOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                        </b-button-group>
                    </b-col>
                </b-row>
                <b-row class="my-2" v-if="allowNotifications">
                    <b-col md="2">
                        <label for="work-orders-notifier-type">Also Notify:</label>
                    </b-col>
                    <b-col>
                        <b-button-group class="work-orders__group">
                            <b-col md="4" class="work-orders__select">
                                <validation-provider name="notifier.type" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-notifier-type"
                                        class="md-2"
                                        v-model="workOrderToSave.notifier.type"
                                        :disabled="!canEdit"
                                        :options="filteredAssigneeNotifierTypeOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                            <b-col class="work-orders__option" v-if="workOrderToSave.notifier.type !== ''">
                                <validation-provider name="notifier.value" v-slot="{ errors }">
                                    <form-select
                                        id="work-orders-notifier-value"
                                        class="md-2"
                                        v-model="workOrderToSave.notifier.value"
                                        :description="
                                            workOrderToSave.notifier.type === ActorType.Vendor
                                                ? vendorOptionsDescription
                                                : ''
                                        "
                                        :disabled="!canEdit"
                                        :options="notifierOptions"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                            </b-col>
                        </b-button-group>
                    </b-col>
                </b-row>
                <b-row class="my-2" v-if="allowNotifications">
                    <b-col class="work-orders__email-details">
                        <form-checkbox v-model="workOrderToSave.emailDetails" :disabled="!canEdit">
                            Email work order details
                        </form-checkbox>
                    </b-col>
                </b-row>
            </div>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-notes">Notes:</label>
                </b-col>
                <b-col>
                    <validation-provider rules="noScript" v-slot="{ errors, handleChange }" name="notes">
                        <form-textarea
                            trim
                            class="mb-2"
                            id="work-orders-notes"
                            rows="4"
                            no-resize
                            v-model="workOrderToSave.notes"
                            :disabled="!canEdit"
                            :error="errors[0]"
                            @input="handleChange"
                        ></form-textarea>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="my-2">
                <b-col md="2">
                    <label for="work-orders-photos">Photos:</label>
                </b-col>
                <b-col>
                    <form-file-drag-and-drop
                        ref="formFileDragAndDrop"
                        :accept="LODGIX_IMAGE_FILES_ALLOWED_EXTENSIONS.join(',')"
                        class="upload-photos"
                        :disabled="!canEdit"
                        :multiple="true"
                        :format-names="formatNames"
                        @input="(photos) => uploadPhotos(photos)"
                        size="lg"
                    />
                    <b-row class="photos">
                        <b-col
                            lg="6"
                            md="12"
                            sm="6"
                            v-for="(photo, i) in workOrderToSave.photos"
                            :key="i"
                            class="photos__photo"
                        >
                            <div class="photos__photo__gallery-image-wrapper">
                                <gallery-image
                                    :id="i"
                                    :resolution="photo.resolution"
                                    :caption="photo.caption"
                                    :edit-caption="canEdit"
                                    download-button
                                    :delete-button="canEdit"
                                    :photo-url="photo.thumbnail_url"
                                    @download="downloadPhoto"
                                    @edit="showEditPhotoCaption"
                                    @delete="deletePhoto"
                                />
                                <div class="photos__photo__gallery-image-wrapper__undo" v-if="photo.deleted">
                                    <button class="photos__photo__gallery-image-wrapper__undo__button" @click="undo(i)">
                                        <font-awesome-icon
                                            class="photos__photo__gallery-image-wrapper__undo__button__icon"
                                            title="Cancel"
                                            :icon="['fas', 'arrow-rotate-left']"
                                        />
                                    </button>
                                </div>
                            </div>
                            <photo-caption-modal
                                :modal-id="editPhotoCaptionModalId + i"
                                :id="i"
                                :caption="workOrderToSave.photos[i].caption"
                                :is-view="!canEdit"
                                @save="onSavePhotoCaption"
                            />
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </validation-observer>
    </b-container>
</template>

<script>
    import API from '../../../../api.jsx';
    import axios from 'axios';
    import { mapGetters, mapActions } from 'vuex';
    import { maskNumber } from '../../../../utils/objects';
    import Datepicker from '../../../generic/datepicker/datepicker.vue';
    import { getFilenameFromResponse, convertBlobResponseToJson, formatResponseError } from '../../../../utils/api';
    import FormFileDragAndDrop from '../../../generic/form-file-drag-and-drop/form-file-drag-and-drop.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormInputGroup from '../../../generic/form-input-group/form-input-group.vue';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';
    import GalleryImage from '../../../common/gallery-image.vue';
    import { getDataURL, getImageResolution } from '../../../../utils/image';
    import { LODGIX_IMAGE_FILES_ALLOWED_EXTENSIONS } from '../../../../constants';
    import PhotoCaptionModal from '../../../common/photo-caption-modal.vue';
    import { WorkOrderActionType, ActorType } from '../../../../constants';

    export default {
        name: 'WorkOrdersProblem',
        components: {
            Datepicker,
            FormFileDragAndDrop,
            FormTextarea,
            FormSelect,
            FormInput,
            FormCheckbox,
            GalleryImage,
            PhotoCaptionModal,
        },
        props: {
            actionType: {
                type: String,
                default: WorkOrderActionType.Create,
            },
            canEdit: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                invalid: false,
                invalidDateRange: false,
                assigneeNotifierTypeOptions: [
                    { value: '', text: 'Not Selected', enabled: true },
                    { value: ActorType.Vendor, text: 'Vendor', enabled: true },
                    {
                        value: ActorType.TeamMember,
                        text: 'Team Member',
                        enabled: true,
                    },
                    { value: ActorType.Owner, text: 'Owner', enabled: true },
                ],
                assigneeOptions: [{ value: '', text: 'Not Selected' }],
                notifierOptions: [{ value: '', text: 'Not Selected' }],
                vendorOptionsDescription: '* indicates no vendor email is present',
                allowNotifications: false,
                editPhotoCaptionModalId: 'edit-photo-caption-modal-id',
                ActorType,
                LODGIX_IMAGE_FILES_ALLOWED_EXTENSIONS,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('workOrders', [
                'priorities',
                'jobTypes',
                'teamMembers',
                'vendors',
                'propertyOwners',
                'workOrderToSave',
            ]),
            priorityOptions() {
                return [
                    ...this.priorities.map((priority) => ({
                        value: priority.id,
                        text: priority.name,
                    })),
                ];
            },
            jobTypeOptions() {
                return [
                    ...this.jobTypes.map((jobType) => ({
                        value: jobType.id,
                        text: jobType.title,
                    })),
                ];
            },
            invoiceVendorOptions() {
                return [
                    { value: '', text: 'Select Vendor' },
                    ...this.vendors.map((vendor) => ({
                        value: vendor.id,
                        text: vendor.company,
                    })),
                ];
            },
            receiptStoreOptions() {
                return [
                    { value: '', text: 'Select Store' },
                    ...this.vendors.map((vendor) => ({
                        value: vendor.id,
                        text: vendor.company,
                    })),
                ];
            },
            filteredAssigneeNotifierTypeOptions() {
                return this.assigneeNotifierTypeOptions.filter((optionType) => optionType.enabled);
            },
        },
        inject: ['setGlobalInvalid'],
        methods: {
            ...mapActions('workOrders', ['updateWorkOrderToSave']),
            setInvalid(isInvalid) {
                this.invalid = isInvalid;
                this.setGlobalInvalid(isInvalid);
            },
            maskInputNumber(evt) {
                return maskNumber(evt);
            },
            mapTeamMemberOwnerSelectOption(reporter = {}) {
                return {
                    value: reporter.id,
                    text: `${reporter.first_name || ''} ${reporter.last_name || ''} (${reporter.email || '-'})`,
                };
            },
            mapAssigneeVendorSelectOption(reporter = {}) {
                return {
                    value: reporter.id,
                    text: reporter.email ? reporter.company || '' : `* ${reporter.company}`,
                };
            },
            mapNotifierVendorSelectOption(reporter = {}) {
                return {
                    value: reporter.id,
                    text: reporter.email ? reporter.company || '' : `* ${reporter.company}`,
                    disabled: !reporter.email,
                };
            },
            mapAssigneeNotifierOptions(optionType = '', isAssigneeOptions = true, options = []) {
                options.splice(0, options.length);
                options.push({
                    value: '',
                    text: 'Not Selected',
                });

                if (optionType === ActorType.TeamMember) {
                    options.push(...this.teamMembers.map(this.mapTeamMemberOwnerSelectOption));
                } else if (optionType === ActorType.Vendor) {
                    options.push(
                        ...this.vendors.map(
                            isAssigneeOptions ? this.mapAssigneeVendorSelectOption : this.mapNotifierVendorSelectOption
                        )
                    );
                } else if (optionType === ActorType.Owner && this.propertyOwners.length) {
                    options.push(...this.propertyOwners.map(this.mapTeamMemberOwnerSelectOption));
                }
            },
            formatNames(files) {
                return files.length === 1 ? 'Uploading your photo' : `Uploading ${files.length} photos`;
            },
            async uploadPhoto(photo) {
                const formData = new FormData();
                formData.append('photo', photo);
                try {
                    const response = await API.post('/app/work-orders/api/orders/upload-photo/', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        responseType: 'blob',
                    });
                    const file = new File([response.data], getFilenameFromResponse(response), {
                        type: response.headers['content-type'],
                    });
                    const [dataURL, imageResolution] = await Promise.all([getDataURL(file), getImageResolution(file)]);
                    this.workOrderToSave.photos.push({
                        file: file,
                        thumbnail_url: dataURL,
                        photo_url: dataURL,
                        photo_name: file.name,
                        resolution: `${imageResolution.width}x${imageResolution.height}`,
                        caption: '',
                        deleted: false,
                    });
                } catch (e) {
                    if (e.response) {
                        e.response.data = await convertBlobResponseToJson(e.response);
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || `Could not download photo ${photo.name}.` });
                }
            },
            async uploadPhotos(photos) {
                if (photos) {
                    this.$emit('busy', true);
                    await Promise.all(photos.map((photo) => this.uploadPhoto(photo)));
                    this.$refs.formFileDragAndDrop.$refs.formFile.$refs.fileInput.reset();
                    this.$emit('busy', false);
                }
            },
            async downloadPhoto(index) {
                try {
                    this.$emit('busy', true);
                    const photo = this.workOrderToSave.photos[index];
                    if (photo.file) {
                        saveAs(photo.photo_url, photo.photo_name);
                    } else {
                        const response = await axios.get(photo.photo_url, { responseType: 'blob' });
                        saveAs(response.data, photo.photo_name);
                    }
                } catch (e) {
                    this.errorToast({ text: 'Could not download photo.' });
                } finally {
                    this.$emit('busy', false);
                }
            },
            deletePhoto(index) {
                if (this.workOrderToSave.photos[index].deleted === undefined) {
                    this.$set(this.workOrderToSave.photos[index], 'deleted', true);
                } else {
                    this.workOrderToSave.photos[index].deleted = true;
                }
            },
            undo(index) {
                this.workOrderToSave.photos[index].deleted = false;
            },
            showEditPhotoCaption(index) {
                this.$bvModal.show(this.editPhotoCaptionModalId + index);
            },
            onSavePhotoCaption(data) {
                this.workOrderToSave.photos[data.id].caption = data.caption;
            },
        },
        watch: {
            // async, updated on rental property select
            propertyOwners(propertyOwners) {
                const ownerOption = this.assigneeNotifierTypeOptions.find(
                    (typeOption) => typeOption.value === ActorType.Owner
                );

                if (propertyOwners.length) {
                    ownerOption.enabled = true;

                    if (this.workOrderToSave.assignee.type === ActorType.Owner) {
                        this.assigneeOptions = [
                            { value: '', text: 'Not Selected' },
                            ...this.propertyOwners.map(this.mapTeamMemberOwnerSelectOption),
                        ];

                        const hasAssigneeId = (_) => {
                            return propertyOwners.map((item) => item.id).includes(this.workOrderToSave.assignee.value);
                        };
                        if (!hasAssigneeId()) {
                            this.workOrderToSave.assignee.value = '';
                        }
                    }

                    if (this.workOrderToSave.notifier.type === ActorType.Owner) {
                        this.notifierOptions = [
                            { value: '', text: 'Not Selected' },
                            ...this.propertyOwners.map(this.mapTeamMemberOwnerSelectOption),
                        ];

                        const hasNotifierId = (_) => {
                            return propertyOwners.map((item) => item.id).includes(this.workOrderToSave.notifier.value);
                        };
                        if (!hasNotifierId()) {
                            this.workOrderToSave.notifier.value = '';
                        }
                    }
                } else {
                    ownerOption.enabled = false;

                    if (this.workOrderToSave.assignee.type === ActorType.Owner) {
                        this.workOrderToSave.assignee.type = '';
                    }
                    if (this.workOrderToSave.notifier.type === ActorType.Owner) {
                        this.workOrderToSave.notifier.type = '';
                    }
                }
            },
            'workOrderToSave.assignee.type': {
                immediate: true,
                handler(assigneeType, prevAssigneeType) {
                    this.mapAssigneeNotifierOptions(assigneeType, true, this.assigneeOptions);

                    if (!assigneeType) {
                        this.workOrderToSave.assignee.value = '';
                        this.workOrderToSave.notifier.type = '';
                    } else {
                        if (prevAssigneeType && assigneeType !== prevAssigneeType) {
                            this.workOrderToSave.assignee.value = '';
                        }
                    }
                },
            },
            'workOrderToSave.notifier.type': {
                immediate: true,
                handler(notifierType, prevNotifierType) {
                    this.mapAssigneeNotifierOptions(notifierType, false, this.notifierOptions);

                    if (!notifierType) {
                        this.workOrderToSave.notifier.value = '';
                    } else {
                        if (prevNotifierType && notifierType !== prevNotifierType) {
                            this.workOrderToSave.notifier.value = '';
                        }
                    }
                },
            },
            'workOrderToSave.assignee.value': {
                immediate: true,
                handler(assigneeValue, prevAssigneeValue) {
                    if (this.workOrderToSave.assignee.type === ActorType.Vendor) {
                        this.allowNotifications = !!this.vendors.find((v) => v.id == assigneeValue)?.email;
                    } else if (this.workOrderToSave.assignee.type !== '') {
                        this.allowNotifications = true;
                    } else this.allowNotifications = false;
                },
            },
            allowNotifications(allowNotifications) {
                if (!allowNotifications) {
                    this.workOrderToSave.emailDetails = false;
                    this.workOrderToSave.notifier.type = '';
                }
            },
            'workOrderToSave.startDate': {
                immediate: true,
                handler(startDate) {
                    if (!!startDate && !!this.workOrderToSave.completedDate) {
                        this.invalidDateRange = new Date(startDate) > new Date(this.workOrderToSave.completedDate);
                    } else {
                        this.invalidDateRange = false;
                    }
                },
            },
            'workOrderToSave.completedDate': {
                immediate: true,
                handler(completedDate) {
                    if (!!this.workOrderToSave.startDate && !!completedDate) {
                        this.invalidDateRange = new Date(completedDate) < new Date(this.workOrderToSave.startDate);
                    } else {
                        this.invalidDateRange = false;
                    }
                },
            },
        },
    };
</script>

<style lang="scss" scoped>
    .upload-photos {
        :deep(.custom-file-input[disabled] ~ .custom-file-label),
        :deep(.custom-file-input:disabled ~ .custom-file-label) {
            background-color: #e9ecef !important;
        }
        :deep(.custom-file-input[disabled], .custom-file-input:disabled) {
            cursor: default;
        }
    }
    .photos {
        margin-top: -1rem;
        padding-bottom: 1px;
        &__photo {
            margin-top: 1rem;
            &__gallery-image-wrapper {
                position: relative;
                &__undo {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(255, 255, 255, 0.75);
                    &__button {
                        width: 2em;
                        height: 2em;
                        background-color: transparent;
                        border: none;
                        outline: none !important;
                        padding: 0;
                        &__icon {
                            opacity: 0.5;
                            transition: 0.3s;
                            width: 100%;
                            height: 100%;
                            &:hover {
                                opacity: 1;
                                transform: scale(1.1);
                            }
                        }
                    }
                }
            }
        }
    }
    .work-orders {
        &__group {
            width: inherit;
        }

        &__select {
            margin-right: 0.2rem;
            padding: 0px !important;
        }

        &__option {
            margin-left: 0.2rem;
            padding: 0px !important;
        }

        &__email-details {
            margin-bottom: 1rem;
        }
    }

    @media (max-width: 770px) {
        .work-orders {
            &__group {
                flex-direction: column;
            }

            &__option {
                margin: 0.5rem 0;
            }
        }
    }
</style>
